// src/data/immigrationGuides/dualCitizenship.js

const dualCitizenship = {
    slug: "dual-citizenship",

    title: "Dual Citizenship UK: Rules, Travel and Applying",

    seo: {
        metaTitle:
            "Dual Citizenship UK 2026: Rules, Travel & How It Works",
        metaDescription:
            "Understand UK dual citizenship (dual nationality): how it works, travelling as a dual national, expired passports, and rules for marriage and children.",
        keywords: [
            "dual citizenship UK",
            "dual nationality UK",
            "British dual citizenship",
            "can I have two citizenships UK"
        ]
    },

    breadcrumbs: [
        {
            name: "Home",
            url: "/"
        },
        {
            name: "Study Guide",
            url: "/study-guide"
        },
        {
            name: "Dual Citizenship",
            url: "/dual-citizenship"
        }
    ],

    introduction: `
[Dual citizenship](https://www.gov.uk/dual-citizenship) (also known as dual
nationality) is allowed in the UK. This means you can be a British citizen and
also a citizen of one or more other countries.

You do not need to apply for dual citizenship. You can apply for foreign citizenship
and keep your British citizenship.
`,

    sections: [
        {
            heading: "Do you need to apply for dual citizenship?",
            content: `
You do not need to apply for dual citizenship separately. You can apply for foreign
citizenship and keep your British citizenship at the same time.

Many countries do not accept dual citizenship. Check with that country's consulate
or embassy in the UK to find out about its rules on dual nationality.

The Home Office does not hold records of everyone who has British citizenship
automatically. Before you travel to the UK, it is your responsibility to check if
you have British citizenship.
`
        },

        {
            heading: "Check if you already have British citizenship",
            content: `
You may have British citizenship automatically if you either:

- were born in the UK
- have a parent with British citizenship

You can check if you have British citizenship on GOV.UK, or apply for British
citizenship if you do not already have it.
`
        },

        {
            heading: "Travelling abroad as a dual national",
            content: `
As a dual national, you cannot get diplomatic help from the British government when
you are in the other country where you also hold citizenship.

For example, if you hold both British and French citizenship, you cannot get
diplomatic help from the UK while you're in France.
`
        },

        {
            heading: "Travelling to the UK as a dual national",
            content: `
As a dual national, you can travel to the UK using either:

- a valid UK passport or Irish passport
- a certificate of entitlement

If you're not a British citizen, you will need permission to travel to the UK, for
example an [electronic travel authorisation (ETA)](https://www.gov.uk/guidance/apply-for-an-electronic-travel-authorisation-eta).

**Travelling with an expired UK passport:** your carrier (for example, your airline)
may allow you to travel if you have both an expired UK passport (issued in 1989 or
later) and a valid passport for a nationality that can get an ETA, and the personal
details on both passports match. It is the carrier's decision whether to allow this.
You cannot get an ETA if you're a British or Irish dual citizen.

**Travelling with another passport or national ID card:** if you got British
citizenship after settling in the UK under the EU Settlement Scheme, you can travel
to the UK using a valid passport of your other nationality, or a national identity
card from the EU, Norway, Iceland, Liechtenstein or Switzerland. Before you travel,
check your eVisa for your pre-settled or settled status, and update your UKVI
account if your passport or ID card has changed.
`
        },

        {
            heading: "Dual citizenship, marriage and children",
            content: `
You do not automatically become a British citizen when you marry a British person.
You need to [apply as the spouse of a British citizen](https://www.gov.uk/settle-in-the-uk).

In some countries, a married person automatically takes their partner's
nationality, and children may automatically have a parent's nationality even if
born abroad. Check with that country's consulate or high commission in the UK to
find out about its rules on dual nationality.
`
        }
    ],

    checklist: [
        "Check if you already have British citizenship automatically",
        "Check the other country's rules on dual nationality",
        "Keep your UKVI account and eVisa details up to date",
        "Know which document you'll travel on to and from the UK"
    ],

    faqs: [
        {
            question: "Does the UK allow dual citizenship?",
            answer:
                "Yes. Dual citizenship (dual nationality) is allowed in the UK: you can be a British citizen and a citizen of other countries at the same time."
        },
        {
            question: "Do I need to apply for dual citizenship?",
            answer:
                "No. You do not need to apply for dual citizenship separately: you can apply for foreign citizenship and keep your British citizenship."
        },
        {
            question: "Will I lose my British citizenship if I get another nationality?",
            answer:
                "Not automatically. But many countries do not accept dual citizenship, so check with that country's consulate or embassy in the UK about its own rules."
        },
        {
            question: "Can I get diplomatic help from the UK in my other country of citizenship?",
            answer:
                "No. As a dual national, you cannot get diplomatic help from the British government while you are in the other country where you also hold citizenship."
        },
        {
            question: "Do I become British automatically if I marry a British citizen?",
            answer:
                "No. You need to apply as the spouse of a British citizen: marriage does not automatically grant British citizenship."
        }
    ],

    relatedPages: [
        {
            title: "British Citizenship Guide",
            url: "/british-citizenship-guide"
        },
        {
            title: "UK eVisa Explained",
            url: "/evisa-explained"
        },
        {
            title: "British Passport Application",
            url: "/british-passport-application"
        }
    ],

    officialSources: [
        {
            title: "GOV.UK - Dual Citizenship",
            url: "https://www.gov.uk/dual-citizenship"
        }
    ],

    metadata: {
        contentType: "guide",
        category: "Immigration Guides",
        author: "Life in UK Coach",
        lastUpdated: "2026-07-28"
    }
};

export default dualCitizenship;
